import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Mail, LifeBuoy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const FAQS = [
  {
    q: "How do I access the labs?",
    a: "Browser labs open in your browser instantly. Advanced labs are network ranges you connect to over OpenVPN — download the .ovpn profile from the lab page and follow the steps shown there.",
  },
  {
    q: "What does the subscription include?",
    a: "One subscription unlocks every lab across all levels, the browser + VPN ranges, and the level certificates. It auto-renews monthly and you can cancel anytime — access continues until the period ends.",
  },
  {
    q: "Which payment methods can I use?",
    a: "Indian customers can pay with UPI Autopay, cards and netbanking (₹ pricing). International customers pay by card ($ pricing). Pick your region on the Subscription page.",
  },
  {
    q: "My VPN lab won't connect — what should I check?",
    a: "Lab sessions expire after 2 hours. If your session expired, relaunch the lab to get a fresh profile. Make sure you imported the latest .ovpn and that your OpenVPN client shows 'Connected' before reaching the target IPs.",
  },
  {
    q: "How do certificates work?",
    a: "Finish every lab in a level and a shareable PDF certificate is issued automatically, with a public verification link and QR code. Add it to LinkedIn straight from your dashboard.",
  },
  {
    q: "Still stuck?",
    a: "Send us a message below or email support@ilfal.com — we read every message and reply by email.",
  },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/contact", form);
      setSent(true);
      toast.success(data?.message || "Message sent — we'll reply by email soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail ? formatApiError(err.response.data.detail) : "Could not send. Please email support@ilfal.com directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="contact-page">
      <div className="mb-10">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2 flex items-center gap-2">
          <LifeBuoy className="w-4 h-4" /> Help &amp; support
        </div>
        <h1 className="text-4xl font-display font-bold tracking-tight text-slate-900 dark:text-white">How can we help?</h1>
        <p className="mt-2 text-slate-600 dark:text-zinc-400">
          Browse the common questions below, or send us a message. You can also email{" "}
          <a href="mailto:support@ilfal.com" data-testid="contact-support-email" className="text-blue-600 font-semibold hover:underline">support@ilfal.com</a>.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <h2 className="text-lg font-display font-bold text-slate-900 dark:text-white mb-4">Frequently asked</h2>
          <Accordion type="single" collapsible className="w-full" data-testid="contact-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left text-sm font-semibold text-slate-800 dark:text-zinc-200">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-slate-600 dark:text-zinc-400 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div>
          <h2 className="text-lg font-display font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
            <Mail className="w-4 h-4 text-blue-600" /> Report an issue
          </h2>
          {sent ? (
            <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-6 flex items-start gap-3" data-testid="contact-success">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
              <div>
                <div className="font-display font-bold text-slate-900">Message received</div>
                <p className="text-sm text-slate-600 mt-1">Thanks for reaching out — we'll reply to your email shortly.</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={() => setSent(false)} data-testid="contact-send-another">Send another</Button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div>
                <Label htmlFor="c-name">Name</Label>
                <Input id="c-name" required value={form.name} onChange={set("name")} placeholder="Your name" data-testid="contact-name" />
              </div>
              <div>
                <Label htmlFor="c-email">Email</Label>
                <Input id="c-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="contact-email" />
              </div>
              <div>
                <Label htmlFor="c-subject">Subject</Label>
                <Input id="c-subject" required value={form.subject} onChange={set("subject")} placeholder="What's this about?" data-testid="contact-subject" />
              </div>
              <div>
                <Label htmlFor="c-message">Message</Label>
                <Textarea id="c-message" required rows={6} value={form.message} onChange={set("message")} placeholder="Describe your question or the issue you ran into…" data-testid="contact-message" />
              </div>
              <Button type="submit" disabled={busy} className="w-full" data-testid="contact-submit">
                {busy ? "Sending…" : "Send message"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
